import {
  useNavigate,
  useParams,
  useSearch,
  type HistoryState,
} from "@tanstack/react-router";
import AdminBreadcrumb from "../AdminBreadcrumb";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

import ClientOverview from "./Tabs/ClientOverview";
import ClientSubscription from "./Tabs/Subscription/ClientSubscription";
import Usage from "./Tabs/Subscription/Usage";
import ActivityLog from "./Tabs/ActivityLog";
import {
  useGetClientOverview,
  useDeactivateClient,
} from "../../api/client.queries";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useCallback, useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import {
  getClientActivityLog,
  getClientSubscription,
  getClientTokens,
} from "../../api/client.api";

export default function ClientDetailContainer() {
  const { clientId } = useParams({ from: "/admin/client/$clientId" });
  const { clientName } = useSearch({ from: "/admin/client/$clientId" });
  const { data: overview } = useGetClientOverview(clientId);
  const navigate = useNavigate();
  const breadcrumbTitle =
    overview?.user.first_name + " " + overview?.user.last_name ||
    clientName ||
    clientId;
  const userId = overview?.user.user_id || clientId;

  const queryClient = useQueryClient();
  const deactivateMutation = useDeactivateClient();
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);

  const openDeactivateDialog = useCallback(() => {
    if (!userId) return;
    setDeactivateDialogOpen(true);
  }, [userId]);

  const runDeactivate = useCallback(async () => {
    if (!userId) return;
    await deactivateMutation.mutateAsync(userId);
    toast.success("Client deactivated successfully");
    navigate({ to: "/admin/client" });
  }, [userId, deactivateMutation, navigate]);

  useEffect(() => {
    if (!userId) return;

    // Prefetch subscription data
    queryClient.prefetchQuery({
      queryKey: ["client-subscription", userId],
      queryFn: () => getClientSubscription(userId),
      staleTime: 10 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
    });

    // Prefetch tokens/usage data
    queryClient.prefetchQuery({
      queryKey: ["client-tokens", userId],
      queryFn: () => getClientTokens(userId),
      staleTime: 5 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    });

    // Prefetch activity log (first page)
    queryClient.prefetchQuery({
      queryKey: ["client-activity-log", userId, 1, 50],
      queryFn: () => getClientActivityLog({ userId, page: 1, pageSize: 50 }),
      staleTime: 5 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    });
  }, [userId, queryClient]);

  const clientData = useGetClientOverview(clientId);
  const isLoading = clientData.isLoading;

  return (
    <main className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-7 overflow-y-auto px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
      {isLoading ? (
        <div className="space-y-1">
          <div className="h-4 w-48 animate-pulse rounded bg-white/10" />
          <div className="h-9 w-64 animate-pulse rounded bg-white/10" />
        </div>
      ) : (
        <AdminBreadcrumb title={breadcrumbTitle} />
      )}
      <div className="min-h-0 flex-1">
        <div className="scrollbar-thin flex min-h-0 w-full flex-row justify-start gap-8 pb-20 md:flex-col xl:flex-row">
          <div className="flex w-auto flex-row gap-2">
            <Button
              icon
              variant="default"
              className="bg-black-950 hover:bg-black-950/80 flex h-[52px] w-auto flex-row gap-2 p-3"
              onClick={() =>
                navigate({
                  to: "/admin/client/edit",
                  search: { clientId, clientName, mode: "edit" },
                  state: { clientData: overview } as HistoryState,
                })
              }
            >
              <i className="ri-edit-line text-md font-light"></i>
              Edit
            </Button>
            <Button
              icon
              variant="default"
              className="bg-black-950 hover:bg-black-950/80 flex h-[52px] w-auto flex-row gap-2 p-3"
              onClick={openDeactivateDialog}
              disabled={deactivateMutation.isPending}
            >
              <i className="ri-delete-bin-line text-md font-light"></i>
              Deactivate
            </Button>
          </div>
          <ConfirmDialog
            open={deactivateDialogOpen}
            onOpenChange={setDeactivateDialogOpen}
            title="Deactivate client"
            description="Deactivate this client? This action cannot be undone."
            confirmLabel="Deactivate"
            onConfirm={runDeactivate}
            isLoading={deactivateMutation.isPending}
          />
          <div className="w-full max-w-[1200px]">
            <Tabs defaultValue="overview">
              <div className="flex flex-row justify-between">
                <TabsList className="bg-black-950 !h-[52px]">
                  <TabsTrigger
                    value="overview"
                    className="data-[state=active]:text-black-950 text-white transition-all duration-200"
                  >
                    Overview
                  </TabsTrigger>
                  <TabsTrigger
                    value="subscription"
                    className="data-[state=active]:text-black-950 text-white transition-all duration-200"
                  >
                    Subscription
                  </TabsTrigger>
                  <TabsTrigger
                    value="usage"
                    className="data-[state=active]:text-black-950 text-white transition-all duration-200"
                  >
                    Usage
                  </TabsTrigger>
                  <TabsTrigger
                    value="activity-log"
                    className="data-[state=active]:text-black-950 text-white transition-all duration-200"
                  >
                    Activity Log
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="overview">
                <ClientOverview data={clientData} />
              </TabsContent>
              <TabsContent value="subscription">
                <ClientSubscription
                  userId={overview?.user.user_id || clientId}
                />
              </TabsContent>
              <TabsContent value="usage">
                <Usage userId={userId} clientName={breadcrumbTitle} />
              </TabsContent>
              <TabsContent value="activity-log">
                <ActivityLog
                  userId={overview?.user.user_id || clientId}
                  clientName={breadcrumbTitle}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </main>
  );
}
